import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, createStyles, Grid, withStyles } from '@material-ui/core';
import dayjs from 'dayjs';
import { mergeDeepRight } from 'ramda';
import { selectCurrentActivityByUserId, selectUserJobs } from 'src/store/selectors/calendar';
import { selectProjectsOptionsList } from 'src/store/selectors/projects';
import { selectModule, selectModuleConfiguration } from 'src/store/selectors/configurations';
import { completeUserCurrentActivity, loadUserCurrentActivity, loadUserJobs, postCalendarEvent } from 'src/store/actions/calendarActions';
import { loadDepartments } from 'src/store/actions/departmentsActions';
import { selectDepartmentsList } from 'src/store/selectors/departments';
import { loadProjectsList } from 'src/store/actions/projectsActions';
import { selectUserData } from 'src/store/selectors/userInfo';
import { selectFromEmployeesListByUserId } from 'src/store/selectors/employees';
import { WTS_MOMENT_DATE_MASK } from 'src/constants/dayjs';
import StartShiftForm from './StartShiftForm';
import AvailableJobsList from './AvailableJobsList';
import EndShiftForm from './EndShiftForm';
import Spinner from '../Loader/Spinner';
const styles = (theme) => createStyles({
    text: {
        color: theme.palette.text.color
    }
});
function WorkShiftManagementForm({ userId }) {
    const [loading, setLoading] = useState(false);
    const [isPendingEventRequest, setPendingEventRequest] = useState(false);
    const [selectedJob, setSelectedJob] = useState();
    const [selectedProjectId, setSelectedProjectId] = useState();
    const [selectedDepartmentId, setSelectedDepartmentId] = useState();
    const [comment, setComment] = useState('');
    const calendarModuleConfig = useSelector((state) => selectModuleConfiguration(state, 'Calendar'));
    const getCurrentActivity = useSelector(selectCurrentActivityByUserId);
    const projectsOptions = useSelector(selectProjectsOptionsList);
    const departments = useSelector(selectDepartmentsList);
    const userJobs = useSelector(selectUserJobs);
    const currentUserData = useSelector(selectUserData);
    const getUserDataById = useSelector(selectFromEmployeesListByUserId);
    // @ts-ignore
    const projectsModuleConfig = useSelector((state) => selectModule(state, 'Projects'));
    const registerWorkOnComputerModuleConfig = useSelector(
    // @ts-ignore
    (state) => selectModule(state, 'RegisterWorkOnComputer'));
    const dispatch = useDispatch();
    const availableJobs = useMemo(() => userJobs.filter((job) => !job.IsLeave), [userJobs]);
    const activeEvent = useMemo(() => getCurrentActivity(userId), [getCurrentActivity, userId]);
    const areCommentsEnabled = calendarModuleConfig?.CommentsEnabled || false;
    const showDepartments = registerWorkOnComputerModuleConfig?.Configuration?.ShowDepartments || false;
    const showProjects = projectsModuleConfig?.Enabled || false;
    useEffect(() => {
        fetchRequiredData();
        preselectDepartmentId();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);
    useEffect(() => {
        if (selectedJob) {
            preselectDepartmentId();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedJob]);
    useEffect(() => {
        if (activeEvent && availableJobs.length) {
            prefillForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeEvent, availableJobs]);
    const fetchRequiredData = () => {
        setLoading(true);
        dispatch(loadUserJobs({ userIds: userId })).finally(() => setLoading(false));
        if (showDepartments && !departments?.length) {
            dispatch(loadDepartments());
        }
        if (showProjects && !projectsOptions?.length) {
            dispatch(loadProjectsList());
        }
    };
    const preselectDepartmentId = () => {
        let departmentIdCandidate;
        if (!userId)
            return;
        if (userId === currentUserData.UserId) {
            departmentIdCandidate = currentUserData.DepartmentId;
        }
        else {
            const managedUser = getUserDataById(userId);
            departmentIdCandidate = managedUser.DepartmentId;
        }
        const departmentId = showDepartments && !checkUserDepartmentIsAllowed(departmentIdCandidate)
            ? undefined
            : departmentIdCandidate;
        setSelectedDepartmentId(departmentId);
    };
    const checkUserDepartmentIsAllowed = (departmentId) => {
        if (!departmentId)
            return false;
        return departmentsWithSelectedJob.some((department) => department.DepartmentId === departmentId);
    };
    const startJob = useCallback(() => {
        const payload = {
            DepartmentId: selectedDepartmentId,
            JobId: selectedJob.JobId,
            StartDate: dayjs().format(WTS_MOMENT_DATE_MASK),
            UserId: userId,
            Comment: comment || null
        };
        setPendingEventRequest(true);
        dispatch(postCalendarEvent(payload))
            .then(() => dispatch(loadUserCurrentActivity(userId)))
            .then(() => setPendingEventRequest(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comment, selectedDepartmentId, selectedJob, userId]);
    const endJob = useCallback(() => {
        const payload = mergeDeepRight({}, activeEvent);
        payload.Event.Comment = comment;
        setPendingEventRequest(true);
        dispatch(completeUserCurrentActivity(payload))
            .then(() => dispatch(loadUserCurrentActivity(userId)))
            .then(() => setPendingEventRequest(false))
            .then(() => setSelectedJob(undefined));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeEvent, comment, userId]);
    const prefillForm = () => {
        const jobToSelect = availableJobs.find((job) => job.JobId === activeEvent.Event?.Job?.Id);
        setSelectedJob(jobToSelect);
        setComment(activeEvent.Event?.Comment || '');
    };
    const departmentSelectHandler = useCallback(([departmentId]) => {
        setSelectedDepartmentId(departmentId);
    }, []);
    const projectSelectHandler = useCallback(([projectId]) => {
        setSelectedProjectId(projectId);
    }, []);
    const departmentsWithSelectedJob = useMemo(() => {
        if (!selectedJob)
            return departments;
        return departments.filter((department) => department.DepartmentJobs?.includes(selectedJob.JobId));
    }, [departments, selectedJob]);
    const availableDepartmentsOptions = useMemo(() => {
        if (!showDepartments)
            return [];
        return departmentsWithSelectedJob.map((department) => ({
            label: department.DepartmentName,
            value: department.DepartmentId
        }));
    }, [showDepartments, departmentsWithSelectedJob]);
    return (React.createElement(Grid, { container: true, spacing: 2, direction: "row", style: { height: '100%' } }, loading
        ? (React.createElement(Box, { display: "flex", justifyContent: "center", width: "100%" },
            React.createElement(Spinner, null)))
        : (React.createElement(React.Fragment, null,
            React.createElement(Grid, { item: true, xs: 6, style: { height: '100%' } },
                React.createElement(AvailableJobsList, { disableSelect: !!activeEvent, selectedJob: selectedJob, availableJobs: availableJobs, onJobSelect: setSelectedJob })),
            React.createElement(Grid, { item: true, xs: 6 }, activeEvent?.Event
                ? (React.createElement(EndShiftForm, { activeEvent: activeEvent, comment: comment, onCommentChange: setComment, enableComment: areCommentsEnabled, onEndShift: endJob, disableShiftButton: isPendingEventRequest }))
                : (React.createElement(StartShiftForm, { isJobSelected: !!selectedJob, showDepartments: showDepartments, selectedDepartment: selectedDepartmentId, departmentsOptions: availableDepartmentsOptions, onDepartmentSelect: departmentSelectHandler, showProjects: showProjects, selectedProject: selectedProjectId, projectsOptions: projectsOptions, onProjectSelect: projectSelectHandler, comment: comment, onCommentChange: setComment, enableComment: areCommentsEnabled, onStartShift: startJob, disableShiftButton: isPendingEventRequest })))))));
}
export default withStyles(styles)(WorkShiftManagementForm);
